

import React from 'react';
import {
    Navigate,
    useLocation,
    useNavigate,
    useOutletContext,
    useParams,
    useRoutes
} from 'react-router-dom';
// layouts
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';
// pages
import LoginPage from './pages/LoginPage';
import Page404 from './pages/Page404';
import DashboardAppPage from './pages/DashboardAppPage';
// grid pages
import MessagesPage from './pages/grids/messages/MessagesPage';
import WalletUsersPage from './pages/grids/wallet/WalletUsersPage';
import WalletUsersKycPage from './pages/grids/wallet/WalletUsersKycPage';
import WalletPinCodesPage from './pages/grids/wallet/WalletPinCodesPage';
import WalletNotificationsPage from './pages/grids/wallet/WalletNotificationsPage';
import WalletAccountsPage from './pages/grids/wallet/WalletAccountsPage';
import WalletWalletsPage from './pages/grids/wallet/WalletWalletsPage';
import WalletTransfersPage from './pages/grids/wallet/WalletTransfersPage';
import WalletEventsPage from './pages/grids/wallet/WalletEventsPage';
import CryptoTopupsPage from './pages/grids/crypto/CryptoTopupsPage';
import CryptoWithdrawalsPage from './pages/grids/crypto/CryptoWithdrawalsPage';
import AccountingSystemAccountsPage from './pages/grids/accounting/AccountingSystemAccountsPage';
import AccountingCryptoCollectionsPage from './pages/grids/accounting/AccountingCryptoCollectionsPage';
import AccountingSystemTransactionsPage from './pages/grids/accounting/AccountingSystemTransactionsPage';
import AccountingExpensesTransactionsPage from './pages/grids/accounting/AccountingExpensesTransactionsPage';
import AccountingRevenueTransactionsPage from './pages/grids/accounting/AccountingRevenueTransactionsPage';
import UsersPage from './pages/grids/UsersPage';
import RolesPage from './pages/grids/RolesPage';
import PermissionEditorPage from './pages/grids/PermissionEditorPage';
import RolesActionsPermissionsPage from './pages/grids/RolesActionsPermissionsPage';
import CountriesPage from './pages/grids/settings/CountriesPage';
import CurrenciesPage from './pages/grids/settings/CurrenciesPage';
import BanksPage from './pages/grids/settings/BanksPage';
import ChainsPage from './pages/grids/crypto/ChainsPage';
import CryptoTokensPage from './pages/grids/crypto/CryptoTokensPage';
import ExportCsvTasksPage from './pages/grids/settings/ExportCsvTasksPage';
import PartnersReferralCodesPage from './pages/grids/partners/PartnersReferralCodesPage';
import PartnersReferralsPage from './pages/grids/partners/PartnersReferralsPage';
import PartnersRewardsPage from './pages/grids/partners/PartnersRewardsPage';
import PartnersPayoutsPage from './pages/grids/partners/PartnersPayoutsPage';

// ----------------------------------------------------------------------

/**
 * @param { ReactComponent } Component is component that will be rendered
 * @param { object } optionalProps is temporary solution for passing props to component
 * @returns {function(*): *} Component with route props
 * */
function withRouteProps(Component, optionalProps = {}) {
    function RouteWrapper(props) {
        const outletCtx = useOutletContext() || {};
        const { setHeaderActions, clearHeaderActions } = outletCtx;

        const params = useParams();
        const navigate = useNavigate();
        const location = useLocation();

        return (
            <Component
                {...props}
                {...optionalProps}
                params={params}
                navigate={navigate}
                location={location}
                setHeaderActions={setHeaderActions}
                clearHeaderActions={clearHeaderActions}
            />
        );
    }
    RouteWrapper.displayName = `withRouteProps(${Component.displayName || Component.name || 'Component'})`;
    return RouteWrapper;
}

export default function Router(props) {
    return useRoutes([
        {
            path: '/app/',
            element: <DashboardLayout />,
            children: [
                { element: <Navigate to="/app/dashboard" />, index: true },
                {
                    path: 'dashboard',
                    Component: withRouteProps(DashboardAppPage, props)
                },
                {
                    path: 'users',
                    Component: withRouteProps(UsersPage, props)
                },
                {
                    path: 'roles',
                    Component: withRouteProps(RolesPage, props)
                },
                {
                    path: 'role-permissions',
                    Component: withRouteProps(PermissionEditorPage, props)
                },
                {
                    path: 'roles-actions-permissions',
                    Component: withRouteProps(RolesActionsPermissionsPage, props)
                },
                {
                    path: 'wallet-users',
                    Component: withRouteProps(WalletUsersPage, props)
                },
                {
                    path: 'wallet-users-kyc',
                    Component: withRouteProps(WalletUsersKycPage, props)
                },
                {
                    path: 'wallet-pin-codes',
                    Component: withRouteProps(WalletPinCodesPage, props)
                },
                {
                    path: 'wallet-notifications',
                    Component: withRouteProps(WalletNotificationsPage, props)
                },
                {
                    path: 'wallet-accounts',
                    Component: withRouteProps(WalletAccountsPage, props)
                },
                {
                    path: 'wallet-wallets',
                    Component: withRouteProps(WalletWalletsPage, props)
                },
                {
                    path: 'wallet-transfers',
                    Component: withRouteProps(WalletTransfersPage, props)
                },
                {
                    path: 'wallet-events',
                    Component: withRouteProps(WalletEventsPage, props)
                },
                {
                    path: 'crypto-topups',
                    Component: withRouteProps(CryptoTopupsPage, props)
                },
                {
                    path: 'crypto-withdrawals',
                    Component: withRouteProps(CryptoWithdrawalsPage, props)
                },
                {
                    path: 'accounting-system-accounts',
                    Component: withRouteProps(AccountingSystemAccountsPage, props)
                },
                {
                    path: 'accounting-crypto-collections',
                    Component: withRouteProps(AccountingCryptoCollectionsPage, props)
                },
                {
                    path: 'accounting-system-transactions',
                    Component: withRouteProps(AccountingSystemTransactionsPage, props)
                },
                {
                    path: 'accounting-expenses-transactions',
                    Component: withRouteProps(AccountingExpensesTransactionsPage, props)
                },
                {
                    path: 'accounting-revenue-transactions',
                    Component: withRouteProps(AccountingRevenueTransactionsPage, props)
                },
                {
                    path: 'countries',
                    Component: withRouteProps(CountriesPage, props)
                },
                {
                    path: 'currencies',
                    Component: withRouteProps(CurrenciesPage, props)
                },
                {
                    path: 'banks',
                    Component: withRouteProps(BanksPage, props)
                },
                {
                    path: 'chains',
                    Component: withRouteProps(ChainsPage, props)
                },
                {
                    path: 'crypto-tokens',
                    Component: withRouteProps(CryptoTokensPage, props)
                },
                {
                    path: 'export-csv-tasks',
                    Component: withRouteProps(ExportCsvTasksPage, props)
                },
                {
                    path: 'partners-referral-codes',
                    Component: withRouteProps(PartnersReferralCodesPage, props)
                },
                {
                    path: 'partners-referrals',
                    Component: withRouteProps(PartnersReferralsPage, props)
                },
                {
                    path: 'partners-rewards',
                    Component: withRouteProps(PartnersRewardsPage, props)
                },
                {
                    path: 'partners-payouts',
                    Component: withRouteProps(PartnersPayoutsPage, props)
                },
            ]
        },
        {
            path: '/messages',
            Component: withRouteProps(MessagesPage, props)
        },
        {
            path: 'login',
            Component: withRouteProps(LoginPage, props)
        },
        {
            element: <SimpleLayout />,
            children: [
                { element: <Navigate to="/app/dashboard" />, index: true },
                { path: '404', Component: withRouteProps(Page404, props) },
                { path: '*', element: <Navigate to="/404" /> }
            ]
        },
        {
            path: '*',
            element: <Navigate to="/404" replace />
        }
    ]);
}
